import React, { useState } from 'react';
import {
    Box, Typography, TextField, Button, Paper,
    InputAdornment, IconButton, Alert, CircularProgress,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { auth, signInWithEmailAndPassword } from '../Services/Firebase/firebaseConfigOuvidoria';
import { useNavigate } from 'react-router-dom';

const NAVY = '#011838';
const BLUE = '#003E87';
const GOLD = '#FDC500';

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': { borderColor: BLUE },
    },
    '& label.Mui-focused': { color: BLUE },
};

function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await signInWithEmailAndPassword(auth, email, password);
            navigate('/painel');
        } catch {
            setError('Falha na autenticação. Verifique suas credenciais.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                bgcolor: NAVY,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
                backgroundImage: `
                    radial-gradient(circle at 20% 80%, rgba(0,62,135,0.4) 0%, transparent 50%),
                    radial-gradient(circle at 80% 20%, rgba(253,197,0,0.08) 0%, transparent 40%)
                `,
            }}
        >
            <Paper
                elevation={24}
                sx={{ width: '100%', maxWidth: 420, borderRadius: 3, overflow: 'hidden' }}
            >
                {/* Brand header */}
                <Box
                    sx={{
                        bgcolor: BLUE,
                        pt: 4,
                        pb: 3,
                        textAlign: 'center',
                        position: 'relative',
                    }}
                >
                    <Box
                        component="img"
                        src="/logo.png"
                        alt="Logo"
                        sx={{
                            width: 180,
                            maxWidth: '80%',
                            height: 'auto',
                            display: 'block',
                            mx: 'auto',
                            mb: 1.5,
                        }}
                    />

                    <Typography
                        sx={{
                            color: 'rgba(255,255,255,0.75)',
                            fontSize: 11,
                            letterSpacing: 3.5,
                            textTransform: 'uppercase',
                        }}
                    >
                        Canal de Ouvidoria
                    </Typography>
                </Box>

                {/* Form body */}
                <Box sx={{ p: 4, bgcolor: '#fff' }}>
                    <Typography variant="subtitle1" sx={{ mb: 3, color: NAVY, fontWeight: 600, fontSize: 15 }}>
                        Acesso Administrativo
                    </Typography>
                    <form onSubmit={handleSubmit}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            <TextField
                                label="E-mail"
                                type="email"
                                variant="outlined"
                                fullWidth
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <EmailIcon sx={{ color: BLUE, fontSize: 20 }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={fieldSx}
                            />
                            <TextField
                                label="Senha"
                                type={showPassword ? 'text' : 'password'}
                                variant="outlined"
                                fullWidth
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LockIcon sx={{ color: BLUE, fontSize: 20 }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                                size="small"
                                                tabIndex={-1}
                                            >
                                                {showPassword
                                                    ? <VisibilityOff fontSize="small" />
                                                    : <Visibility fontSize="small" />
                                                }
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={fieldSx}
                            />

                            {error && (
                                <Alert severity="error" sx={{ borderRadius: 2, py: 0.5, fontSize: 13 }}>
                                    {error}
                                </Alert>
                            )}

                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                disabled={loading}
                                sx={{
                                    bgcolor: BLUE,
                                    py: 1.5,
                                    mt: 0.5,
                                    fontSize: 14,
                                    fontWeight: 700,
                                    letterSpacing: 1.5,
                                    textTransform: 'uppercase',
                                    borderRadius: 2,
                                    boxShadow: 'none',
                                    '&:hover': { bgcolor: NAVY, boxShadow: 'none' },
                                    '&:disabled': { bgcolor: '#ccc' },
                                }}
                            >
                                {loading
                                    ? <CircularProgress size={22} sx={{ color: '#fff' }} />
                                    : 'Entrar'
                                }
                            </Button>

                            <Button
                                variant="text"
                                fullWidth
                                onClick={() => navigate('/registrar')}
                                sx={{
                                    color: '#999',
                                    fontSize: 12,
                                    textTransform: 'none',
                                    '&:hover': { color: BLUE, bgcolor: 'transparent' },
                                }}
                            >
                                Criar nova conta
                            </Button>
                        </Box>
                    </form>
                </Box>

                {/* Footer */}
                <Box sx={{ py: 1.5, textAlign: 'center', bgcolor: '#f5f6f8', borderTop: '1px solid #eee' }}>
                    <Typography sx={{ fontSize: 11, color: '#bbb', letterSpacing: 0.3 }}>
                        © {new Date().getFullYear()} Metrum — Uso exclusivo interno
                    </Typography>
                </Box>
            </Paper>
        </Box>
    );
}

export default LoginPage;
